import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TechLabel } from '../components/common/TacticalCard';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Giriş başarılı');
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_cd0460ea-ffc2-439c-bd21-54fcb026442d/artifacts/xso0m373_logo.png" 
              alt="VAV Wear" 
              className="h-16 mx-auto mb-6"
            />
          </Link>
          <TechLabel variant="accent" className="mb-4">[GİRİŞ]</TechLabel>
          <h1 className="font-heading text-3xl text-vav-fg">HESABINIZA GİRİN</h1>
        </div>

        {/* Login Form */}
        <div className="tactical-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-2">E-posta</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vav-fg-dark" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none"
                  placeholder="ornek@email.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-2">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vav-fg-dark" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-11 pr-11 py-3 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-vav-fg-dark hover:text-vav-fg"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-vav-accent-cyan hover:underline">
                Şifremi Unuttum
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full tactical-btn flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? 'GİRİŞ YAPILIYOR...' : 'GİRİŞ YAP'}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-vav-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-vav-bg-paper text-sm text-vav-fg-muted">veya</span>
            </div>
          </div>

          {/* Google Login */}
          <button
            onClick={loginWithGoogle}
            className="w-full tactical-btn-outline flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google ile Giriş Yap
          </button>

          {/* Register Link */}
          <p className="text-center mt-8 text-sm text-vav-fg-muted">
            Hesabınız yok mu?{' '}
            <Link to="/register" className="text-vav-accent-cyan hover:underline">
              Kayıt Olun
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
};

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(name, email, password);
      toast.success('Kayıt başarılı');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_cd0460ea-ffc2-439c-bd21-54fcb026442d/artifacts/xso0m373_logo.png" 
              alt="VAV Wear" 
              className="h-16 mx-auto mb-6"
            />
          </Link>
          <TechLabel variant="accent" className="mb-4">[KAYIT]</TechLabel>
          <h1 className="font-heading text-3xl text-vav-fg">HESAP OLUŞTURUN</h1>
        </div>

        {/* Register Form */}
        <div className="tactical-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-2">Ad Soyad</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vav-fg-dark" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none"
                  placeholder="Ad Soyad"
                  data-testid="name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-2">E-posta</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vav-fg-dark" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none"
                  placeholder="ornek@email.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-2">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vav-fg-dark" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-11 pr-11 py-3 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none"
                  placeholder="En az 6 karakter"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-vav-fg-dark hover:text-vav-fg"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-2">Şifre Tekrar</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vav-fg-dark" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none"
                  placeholder="Şifrenizi tekrar girin"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full tactical-btn flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? 'KAYIT YAPILIYOR...' : 'KAYIT OL'}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-vav-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-vav-bg-paper text-sm text-vav-fg-muted">veya</span>
            </div>
          </div>

          {/* Google Register */}
          <button
            onClick={loginWithGoogle}
            className="w-full tactical-btn-outline flex items-center justify-center gap-3"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google ile Kayıt Ol
          </button>

          {/* Login Link */}
          <p className="text-center mt-8 text-sm text-vav-fg-muted">
            Zaten hesabınız var mı?{' '}
            <Link to="/login" className="text-vav-accent-cyan hover:underline">
              Giriş Yapın
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
};

export { Login, Register };
